import { useEffect, useState } from 'react'
import { CogoLogoBrand } from '@/components/cogo-logo'
import { useProjectThumbnailSrc } from '@/features/project/hooks/use-project-thumbnail-src'
import { cn } from '@/lib/utils'
import { useStudioLocale } from '@/i18n/use-studio-locale'

interface AppThumbnailPreviewProps {
  title: string
  imageSrc?: string | null
  alt?: string
  className?: string
  imageClassName?: string
  kind?: 'project' | 'template' | 'library'
  /**
   * The project these art bytes belong to. Supplying it lets the card show a
   * thumbnail the studio captured from its own render of the project, and lets
   * a stored cross-origin artifact URL be routed through the studio-origin
   * proxy instead of being refused outright. Omit it for template/library art,
   * which is already served from this origin.
   */
  projectId?: string | null
}

export function AppThumbnailPreview({
  title,
  imageSrc,
  alt,
  className,
  imageClassName,
  kind = 'project',
  projectId,
}: AppThumbnailPreviewProps) {
  const [imgError, setImgError] = useState(false)
  const resolvedSrc = useProjectThumbnailSrc(imageSrc, projectId)
  const safeImageSrc = safeThumbnailImageSrc(resolvedSrc)
  const showImage = Boolean(safeImageSrc) && !imgError

  // A newly captured thumbnail must not inherit the previous src's failure.
  useEffect(() => { setImgError(false) }, [safeImageSrc])
  const initials = title.trim().slice(0, 2).toUpperCase() || 'CG'
  // The kind label is the only text this component renders on its own, so it
  // has to come from the dictionary like the copy around it — otherwise a
  // fully-Korean Library still ends on an English "Library preview". The img
  // alt fallback was the same hole: `${title} preview` leaked the English
  // word onto the Korean templates screen via the alt attribute.
  const { strings } = useStudioLocale()
  const kindPreviewLabel =
    kind === 'template'
      ? strings.library.thumbnailPreview.template
      : kind === 'library'
        ? strings.library.thumbnailPreview.library
        : strings.library.thumbnailPreview.project

  return (
    <div
      data-testid="app-thumbnail-preview"
      data-thumbnail-kind={kind}
      className={cn(
        'relative aspect-[16/10] overflow-hidden rounded-xl border border-border/70 bg-muted',
        className,
      )}
    >
      {showImage ? (
        <img
          src={safeImageSrc ?? undefined}
          alt={alt ?? `${title} ${kindPreviewLabel}`}
          loading="lazy"
          onError={() => setImgError(true)}
          className={cn('h-full w-full object-cover object-top', imageClassName)}
        />
      ) : (
        <div
          data-thumbnail-fallback="cogo-play"
          className="flex h-full w-full flex-col bg-[linear-gradient(135deg,hsl(var(--background)),hsl(var(--accent)/0.48))]"
        >
          <div className="flex flex-1 items-center justify-center p-4">
            <div
              data-brand-mark="cogo-play-thumbnail"
              className="flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-background/95 shadow-sm"
            >
              <CogoLogoBrand size={38} variant="speed" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-border/70 bg-background/80 px-3 py-2 text-[10px] font-semibold backdrop-blur">
            <span className="truncate text-foreground">{kindPreviewLabel}</span>
            <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{initials}</span>
          </div>
        </div>
      )}
    </div>
  )
}

function safeThumbnailImageSrc(imageSrc?: string | null): string | null {
  if (!imageSrc) return null
  const trimmed = imageSrc.trim()
  if (!trimmed) return null
  if (trimmed.startsWith('/') || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed

  if (typeof window === 'undefined') return null
  try {
    const url = new URL(trimmed, window.location.origin)
    return url.origin === window.location.origin ? url.toString() : null
  } catch {
    return null
  }
}
